import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { InternalLinks } from "@/components/shared/InternalLinks";
import { getCareerPositions, getPageContent } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import {
  Search, MapPin, Briefcase, ChevronRight, Loader2, Users, Star,
  Shield, TrendingUp, Award, Heart, Clock,
} from "lucide-react";

const POSITION_TYPE_LABELS = {
  sales_consultant: "Sales Consultant",
  sales_rep_pt: "Sales Consultant (Part-Time)",
  assistant_manager: "Assistant Manager",
  store_manager: "Store Manager",
};

const CAREER_VIDEOS = [
  { src: "/api/static-video/careers_clip_1.mp4", poster: "/api/static-video/careers_clip_1.jpg" },
  { src: "/api/static-video/careers_clip_2.mp4", poster: "/api/static-video/careers_clip_2.jpg" },
  { src: "/api/static-video/careers_clip_3.mp4", poster: "/api/static-video/careers_clip_3.jpg" },
  { src: "/api/static-video/careers_clip_4.mp4", poster: "/api/static-video/careers_clip_4.jpg" },
  { src: "/api/static-video/careers_clip_5.mp4", poster: "/api/static-video/careers_clip_5.jpg" },
];

const SITE_URL = "https://primemediawireless.com";
const CAREER_VIDEO_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": CAREER_VIDEOS.map((v, i) => ({
    "@type": "VideoObject",
    "name": `Prime Media Wireless Careers — Clip ${i + 1}`,
    "description": "Behind the scenes at Prime Media Wireless retail stores — join our team as a Total Wireless dealer, sales associate, or store manager.",
    "thumbnailUrl": `${SITE_URL}${v.poster}`,
    "contentUrl": `${SITE_URL}${v.src}`,
    "uploadDate": "2025-01-01",
    "publisher": {
      "@type": "Organization",
      "name": "Prime Media Wireless",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo512.png`,
      },
    },
  })),
};

export default function CareersPage() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [currentClip, setCurrentClip] = useState(0);
  const [cmsBenefits, setCmsBenefits] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    getCareerPositions(true).then(setPositions).catch(console.error).finally(() => setLoading(false));
    getPageContent("careers").then((sections) => {
      const benefitsSection = sections.find((s) => s.section_name === "benefits");
      if (benefitsSection?.metadata?.items?.length) {
        setCmsBenefits(benefitsSection.metadata.items);
      }
    }).catch(() => {});
  }, []);

  const handleVideoEnd = useCallback(() => {
    setCurrentClip((prev) => (prev + 1) % CAREER_VIDEOS.length);
  }, []);

  const states = useMemo(() => {
    const s = new Set(positions.map((p) => p.location_state).filter(Boolean));
    return [...s].sort();
  }, [positions]);

  const filtered = useMemo(() => {
    return positions.filter((p) => {
      const matchType = filterType === "all" || p.position_type === filterType;
      const matchState = filterState === "all" || p.location_state === filterState;
      const matchSearch = !search ||
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        (p.location_city || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.location_state || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.location_name || "").toLowerCase().includes(search.toLowerCase());
      return matchType && matchState && matchSearch;
    });
  }, [positions, filterType, filterState, search]);

  const ICON_MAP = { TrendingUp, Heart, Award, Users, Shield, Star, Briefcase, Clock, MapPin, Search };
  const defaultBenefits = [
    { icon: TrendingUp, title: "Career Growth", desc: "Clear paths to management and leadership roles" },
    { icon: Heart, title: "Health Benefits", desc: "Comprehensive medical, dental, and vision coverage" },
    { icon: Award, title: "Competitive Pay", desc: "Base salary plus uncapped commission potential" },
    { icon: Users, title: "Team Culture", desc: "Supportive environment that celebrates success" },
    { icon: Shield, title: "Paid Training", desc: "Full training program from day one" },
    { icon: Star, title: "Employee Perks", desc: "Discounts on wireless services and devices" },
  ];

  const benefits = cmsBenefits
    ? cmsBenefits.map((b) => ({
        icon: ICON_MAP[b.icon] || Star,
        title: b.title || "",
        desc: b.description || "",
      }))
    : defaultBenefits;

  return (
    <PublicLayout>
      <SEOHead
        title="Careers | Prime Media Wireless"
        description="Join the Prime Media Wireless team. Explore open positions at our retail locations nationwide — Sales Consultants, Assistant Managers, and Store Managers."
        pageSlug="careers"
      />

      {/* Google VideoObject schema for career hero clips — helps with
          video indexing in Search Console ("thumbnailUrl" field). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(CAREER_VIDEO_SCHEMA) }}
      />

      {/* Hero */}
      <section className="bg-black py-16 lg:py-24 relative overflow-hidden min-h-[420px]" data-testid="careers-hero">
        {/* Video Background */}
        <video
          key={currentClip}
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          poster={CAREER_VIDEOS[currentClip].poster}
          muted
          autoPlay
          playsInline
          preload="auto"
          onEnded={handleVideoEnd}
          onError={(e) => {
            console.error("Video error:", e.target.error);
            // Skip to next clip on error
            handleVideoEnd();
          }}
          data-testid="careers-hero-video"
        >
          <source src={CAREER_VIDEOS[currentClip].src} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/35" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-block px-4 py-1.5 bg-teal/20 text-teal rounded-full text-sm font-body font-medium mb-6">
              We're Hiring
            </span>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
              Build Your Career with Prime Media Wireless
            </h1>
            <p className="text-slate-300 font-body text-base lg:text-lg mb-8 max-w-2xl mx-auto">
              Join the nation's premier Total Wireless master agent. We're looking for motivated individuals to help us connect communities with affordable, no-contract wireless services.
            </p>
          </motion.div>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by title, city, or state..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-full text-base"
              data-testid="careers-search"
            />
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-14 bg-white border-b border-slate-200" data-testid="careers-benefits">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl text-navy text-center mb-10">Why Join Prime Media Wireless?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex items-start gap-3 p-4 rounded-xl border border-slate-100 hover:border-teal/30 transition-colors"
              >
                <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <b.icon className="w-5 h-5 text-teal" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-navy text-sm">{b.title}</h3>
                  <p className="text-slate-500 font-body text-xs mt-0.5">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters + Job Listings */}
      <section className="py-12 lg:py-16 bg-slate-50" data-testid="careers-listings">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <h2 className="font-heading font-bold text-2xl text-navy">
              Open Positions <span className="text-teal text-lg font-normal ml-1">({filtered.length})</span>
            </h2>
            <div className="flex flex-wrap gap-3">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm font-body bg-white"
                data-testid="careers-filter-type"
              >
                <option value="all">All Positions</option>
                <option value="sales_consultant">Sales Consultant</option>
                <option value="sales_rep_pt">Sales Consultant (Part-Time)</option>
                <option value="assistant_manager">Assistant Manager</option>
                <option value="store_manager">Store Manager</option>
              </select>
              <select
                value={filterState}
                onChange={(e) => setFilterState(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm font-body bg-white"
                data-testid="careers-filter-state"
              >
                <option value="all">All States</option>
                {states.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-teal mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="careers-empty">
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-navy text-xl mb-2">
                {search || filterType !== "all" || filterState !== "all" ? "No matching positions" : "No open positions"}
              </h3>
              <p className="text-slate-500 font-body">
                {search ? "Try adjusting your search or filters." : "Check back soon for new opportunities."}
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filtered.map((pos) => (
                <Link
                  key={pos.id}
                  to={`/careers/${pos.slug}`}
                  className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-teal/40 hover:shadow-md transition-all group"
                  data-testid={`job-card-${pos.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-2.5 py-0.5 bg-teal/10 text-teal text-xs font-medium rounded-full">
                          {POSITION_TYPE_LABELS[pos.position_type] || pos.position_type}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="w-3 h-3" /> {pos.employment_type}
                        </span>
                      </div>
                      <h3 className="font-heading font-bold text-navy text-lg group-hover:text-teal transition-colors">{pos.title}</h3>
                      <div className="flex items-center gap-1 mt-1 text-slate-500 text-sm font-body">
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span>{pos.location_name || "Location"} — {pos.location_city}, {pos.location_state}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal transition-colors flex-shrink-0 mt-2" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 bg-white border-t border-slate-200" data-testid="careers-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-2xl text-navy mb-3">Don't See Your Perfect Role?</h2>
          <p className="text-slate-600 font-body mb-6">We're always looking for talented people. Send us your resume and we'll keep you in mind for future opportunities.</p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-teal text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-teal/90 transition-colors"
            data-testid="careers-contact-cta"
          >
            Contact Us
          </a>
        </div>
      </section>
      <InternalLinks exclude="/careers" />
    </PublicLayout>
  );
}
